import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ChevronRight, Globe, Loader2, AlertCircle, Calendar, CheckCircle,
} from "lucide-react";
import { toast } from "react-hot-toast";
import webScraperService, {
  WebScrapeJob,
  WebScraperJobStatus,
  WebScraperItemStatus,
  WebScraperItemType,
  ImportResult,
} from "../../services/web-scraper.service";
import documentStoresService from "../../services/document-stores.service";
import JobStatusBadge from "../../components/documents/web-scraper/JobStatusBadge";
import JobStatsBar from "../../components/documents/web-scraper/JobStatsBar";
import JobActionButtons from "../../components/documents/web-scraper/JobActionButtons";
import ImportCTABanner from "../../components/documents/web-scraper/ImportCTABanner";
import LiveProgressPanel from "../../components/documents/web-scraper/LiveProgressPanel";
import DiscoveredItemsTable from "../../components/documents/web-scraper/DiscoveredItemsTable";
import CrawlTreeView from "../../components/documents/web-scraper/CrawlTreeView";
import SkippedUrlsPanel from "../../components/documents/web-scraper/SkippedUrlsPanel";
import ImportConfirmationDialog from "../../components/documents/web-scraper/ImportConfirmationDialog";
import DeleteConfirmModal from "../../components/documents/DeleteConfirmModal";

export default function WebScraperJobDetail() {
  const { documentStoreId, jobId } = useParams<{ documentStoreId: string; jobId: string }>();
  const navigate = useNavigate();

  const [storeName, setStoreName] = useState("");
  const [job, setJob] = useState<WebScrapeJob | null>(null);
  const [loadingJob, setLoadingJob] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [loadingAction, setLoadingAction] = useState<string | undefined>();

  // user-skipped — DiscoveredItem rows the user clicked "skip" on
  const [userSkippedCount, setUserSkippedCount] = useState(0);
  // crawler-skipped — URLs the engine dropped during the crawl (shown in stats card)
  const [crawlerSkippedCount, setCrawlerSkippedCount] = useState(0);
  // importable totals split by kind — pages vs documents (FILE + IMAGE) — so the CTA can show both
  const [importablePageCount, setImportablePageCount] = useState(0);
  const [importableDocumentCount, setImportableDocumentCount] = useState(0);
  const [tableRefreshKey, setTableRefreshKey] = useState(0);
  const [activeTab, setActiveTab] = useState<"discovered" | "tree" | "skipped">("discovered");

  // Tree View is only meaningful once the crawl has finished — gate it
  const isTreeAvailable = job?.status === WebScraperJobStatus.DONE;

  // If the user was on the Tree tab and the job is no longer DONE (status changed
  // out from under them), drop back to Discovered so they don't stare at a hidden tab
  useEffect(() => {
    if (activeTab === "tree" && !isTreeAvailable) {
      setActiveTab("discovered");
    }
  }, [activeTab, isTreeAvailable]);

  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Import dialog state
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importDto, setImportDto] = useState<{ item_ids?: string[]; import_all?: boolean } | null>(null);
  const [importCount, setImportCount] = useState(0);
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  const prevJobStatusRef = useRef<WebScraperJobStatus | undefined>(undefined);

  useEffect(() => {
    if (!documentStoreId) return;
    documentStoresService.getDocumentStoreById(documentStoreId)
      .then((store) => setStoreName(store.folder_name))
      .catch(() => {});
  }, [documentStoreId]);

  const fetchSkippedCount = useCallback(async () => {
    if (!jobId) return;
    try {
      const [userSkipped, crawlerSkipped, pages, files, images] = await Promise.all([
        webScraperService.listItems(jobId, { status: WebScraperItemStatus.SKIPPED, skip: 0, limit: 1 }),
        webScraperService.listSkippedUrls(jobId, { skip: 0, limit: 1 }),
        webScraperService.listItems(jobId, { status: WebScraperItemStatus.DISCOVERED, item_type: WebScraperItemType.PAGE, skip: 0, limit: 1 }),
        webScraperService.listItems(jobId, { status: WebScraperItemStatus.DISCOVERED, item_type: WebScraperItemType.FILE, skip: 0, limit: 1 }),
        webScraperService.listItems(jobId, { status: WebScraperItemStatus.DISCOVERED, item_type: WebScraperItemType.IMAGE, skip: 0, limit: 1 }),
      ]);
      setUserSkippedCount(userSkipped.total);
      setCrawlerSkippedCount(crawlerSkipped.total);
      setImportablePageCount(pages.total);
      setImportableDocumentCount(files.total + images.total);
    } catch {
      // non-fatal
    }
  }, [jobId]);

  const fetchJob = useCallback(async () => {
    if (!jobId) return;
    try {
      const data = await webScraperService.getJobById(jobId);
      setJob(data);
    } catch {
      toast.error("Failed to load job");
    } finally {
      setLoadingJob(false);
    }
  }, [jobId]);

  // Initial load
  useEffect(() => {
    fetchJob();
    fetchSkippedCount();
  }, [fetchJob, fetchSkippedCount]);

  // Polling while RUNNING — refresh both job stats and items table every 5s
  useEffect(() => {
    if (!job || job.status !== WebScraperJobStatus.RUNNING) return;
    const interval = setInterval(() => {
      if (!actionLoading) {
        fetchJob();
        setTableRefreshKey((k) => k + 1);
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [job?.status]);

  // Refresh discovered items table when job stops running
  useEffect(() => {
    if (!job) return;
    if (prevJobStatusRef.current === WebScraperJobStatus.RUNNING && job.status !== WebScraperJobStatus.RUNNING) {
      setTableRefreshKey((k) => k + 1);
      fetchSkippedCount();
    }
    prevJobStatusRef.current = job.status;
  }, [job?.status, fetchSkippedCount]);

  const runAction = async (action: string, fn: () => Promise<void>) => {
    setActionLoading(true);
    setLoadingAction(action);
    try {
      await fn();
      await fetchJob();
    } catch (err: any) {
      const msg = err?.response?.data?.error?.error_description || "Action failed";
      toast.error(msg);
    } finally {
      setActionLoading(false);
      setLoadingAction(undefined);
    }
  };

  const handleStart = () => runAction("start", () => webScraperService.startJob(jobId!).then(() => {}));
  const handlePause = () => runAction("pause", () => webScraperService.pauseJob(jobId!));
  const handleResume = () => runAction("resume", () => webScraperService.resumeJob(jobId!).then(() => {}));
  const handleCancel = () => runAction("cancel", () => webScraperService.cancelJob(jobId!));

  const handleDelete = () => setShowDeleteModal(true);

  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    try {
      await webScraperService.deleteJob(jobId!);
      toast.success("Job deleted");
      navigate(`/files/${documentStoreId}/web-scraper`);
    } catch (err: any) {
      const msg = err?.response?.data?.error?.error_description || "Failed to delete job";
      toast.error(msg);
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const openImportDialog = (dto: { item_ids?: string[]; import_all?: boolean }, count: number) => {
    setImportDto(dto);
    setImportCount(count);
    setImportResult(null);
    setImportDialogOpen(true);
  };

  const handleImportAll = () => {
    openImportDialog({ import_all: true }, importablePageCount + importableDocumentCount);
  };

  const handleImportSelected = (dto: { item_ids?: string[]; import_all?: boolean }) => {
    openImportDialog(dto, dto.item_ids?.length ?? 0);
  };

  const handleConfirmImport = async () => {
    if (!importDto || !jobId) return;
    setIsImporting(true);
    try {
      const result = await webScraperService.importItems(jobId, importDto);
      setImportResult(result);
      if (result.imported > 0) toast.success(`${result.imported} file(s) imported`);
      if (result.failed > 0) toast.error(`${result.failed} file(s) failed`);
      fetchJob();
      fetchSkippedCount();
      setTableRefreshKey((k) => k + 1);
    } catch {
      toast.error("Import failed");
      setImportDialogOpen(false);
    } finally {
      setIsImporting(false);
    }
  };

  const handleCloseImport = () => {
    setImportDialogOpen(false);
    setImportResult(null);
  };

  const formatDateTime = (iso: string | null) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  };

  if (loadingJob) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
      </div>
    );
  }

  if (!job) {
    return (
      <div className="p-6 text-center text-gray-400">
        <AlertCircle className="w-10 h-10 mx-auto mb-2" />
        <p>Job not found</p>
      </div>
    );
  }

  return (
    <>
      <div className="mx-auto space-y-3">
      {/* Control Panel */}
      <div className="bg-white border border-gray-200 px-5 py-4 space-y-3">
        {/* Header row */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Globe className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-base font-bold text-gray-900 break-all">{job.seed_url}</h1>
                <JobStatusBadge status={job.status} />
                {job.status === WebScraperJobStatus.RUNNING && (
                  <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
                )}
              </div>
              <p className="text-xs text-gray-500 mt-0.5">Domain: {job.allowed_domain}</p>
            </div>
          </div>
          <button
            onClick={() => navigate(`/files/${documentStoreId}/web-scraper`)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-blue-200 bg-blue-50 text-blue-600 text-sm font-medium hover:bg-blue-100 hover:border-blue-300 active:scale-95 transition-all flex-shrink-0"
            type="button"
          >
            <ChevronRight className="w-3.5 h-3.5 rotate-180" />
            Back to Web Scraper
          </button>
        </div>

        {/* Timestamps */}
        <div className="flex items-center gap-4 text-xs text-gray-500 flex-wrap">
          <span className="flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5" />
            Created: {formatDateTime(job.createdAt)}
          </span>
          {job.started_at && (
            <span className="flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5" />
              Started: {formatDateTime(job.started_at)}
            </span>
          )}
          {job.completed_at && (
            <span className="flex items-center gap-1.5">
              <CheckCircle className="w-3.5 h-3.5 text-green-500" />
              Completed: {formatDateTime(job.completed_at)}
            </span>
          )}
        </div>

        {/* Error message */}
        {job.status === WebScraperJobStatus.FAILED && job.error_message && (
          <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
            <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{job.error_message}</p>
          </div>
        )}

        {/* Stats — "Skipped" card reflects everything that didn't make it into the library:
            URLs the crawler dropped + items the user manually skipped */}
        <JobStatsBar job={job} skippedCount={crawlerSkippedCount + userSkippedCount} />

        {/* Actions */}
        <JobActionButtons
          job={job}
          onStart={handleStart}
          onPause={handlePause}
          onResume={handleResume}
          onCancel={handleCancel}
          onDelete={handleDelete}
          isLoading={actionLoading}
          loadingAction={loadingAction}
        />
      </div>

      {/* Live progress — only visible while the job is RUNNING */}
      <LiveProgressPanel job={job} />

      {/* Import CTA — only when there are items ready to bring in */}
      <ImportCTABanner
        pageCount={importablePageCount}
        documentCount={importableDocumentCount}
        storeName={storeName}
        onImportAll={handleImportAll}
        isLoading={actionLoading && loadingAction === "importAll"}
      />

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-gray-200 bg-white px-2">
        <button
          onClick={() => setActiveTab("discovered")}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
            activeTab === "discovered"
              ? "border-purple-600 text-purple-700"
              : "border-transparent text-gray-600 hover:text-gray-800"
          }`}
        >
          Discovered Items
        </button>
        {isTreeAvailable && (
          <button
            onClick={() => setActiveTab("tree")}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === "tree"
                ? "border-purple-600 text-purple-700"
                : "border-transparent text-gray-600 hover:text-gray-800"
            }`}
          >
            Tree View
          </button>
        )}
        <button
          onClick={() => setActiveTab("skipped")}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
            activeTab === "skipped"
              ? "border-purple-600 text-purple-700"
              : "border-transparent text-gray-600 hover:text-gray-800"
          }`}
        >
          Skipped URLs
        </button>
      </div>

      {activeTab === "discovered" && (
        <DiscoveredItemsTable
          jobId={job._id}
          isJobRunning={job.status === WebScraperJobStatus.RUNNING}
          refreshKey={tableRefreshKey}
          onImport={handleImportSelected}
          onSkip={() => { fetchJob(); fetchSkippedCount(); }}
        />
      )}
      {activeTab === "tree" && isTreeAvailable && (
        <CrawlTreeView
          jobId={job._id}
          seedUrl={job.seed_url}
          refreshKey={tableRefreshKey}
        />
      )}
      {activeTab === "skipped" && (
        <SkippedUrlsPanel jobId={job._id} refreshKey={tableRefreshKey} />
      )}

      </div>

      {/* Modals rendered outside space-y wrapper to avoid inherited margin-top */}
      <ImportConfirmationDialog
        isOpen={importDialogOpen}
        onClose={handleCloseImport}
        onConfirm={handleConfirmImport}
        itemCount={importCount}
        storeName={storeName}
        isImporting={isImporting}
        importResult={importResult}
      />
      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        itemName={job.seed_url}
        itemType="Scrape Job"
        isDeleting={isDeleting}
      />
    </>
  );
}
